import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

declare global {
  interface Window {
    LOKALISE_CONFIG?: {
      projectId: string;
      locale: string;
      plainKey: boolean;
      syncTranslations: boolean;
    };
  }
}

type Props = {
  enabled: boolean;
  projectId: string | undefined;
};

/**
 * Loads Lokalise LiveJS in-context editor script when enabled.
 * The editor allows translators to edit translations directly on the webpage.
 *
 * @see https://docs.lokalise.com/en/articles/1592525-livejs-web-in-context-editor
 */
export function LokaliseInContextEditor({ enabled, projectId }: Props) {
  const { i18n } = useTranslation();
  const isInitializedRef = useRef(false);

  // Load Lokalise script only once
  useEffect(() => {
    if (!enabled || !projectId) {
      return;
    }

    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    // Set up Lokalise configuration
    window.LOKALISE_CONFIG = {
      projectId,
      locale: i18n.language,
      plainKey: true,
      syncTranslations: true,
    };

    // Inject styles to fix Lokalise modal transparency
    const style = document.createElement("style");
    style.id = "lokalise-in-context-editor-styles";
    style.textContent = `
      #lokalise-live-js-login .lokalise-live-js-container {
        background-color: #fff !important;
      }
    `;
    document.head.appendChild(style);

    // Fix broken image sources in Lokalise toolbar
    // The Lokalise script incorrectly prepends URL to base64 data URIs
    const fixLokaliseImages = () => {
      const images = document.querySelectorAll(
        '[class*="lokalise"] img, [id*="lokalise"] img',
      );
      images.forEach((img) => {
        const src = img.getAttribute("src");
        if (src?.includes("static.lokalise.com/data:")) {
          const fixedSrc = src.replace(
            /^https?:\/\/static\.lokalise\.com\//,
            "",
          );
          img.setAttribute("src", fixedSrc);
        }
      });
    };

    // Use MutationObserver to fix images as Lokalise adds them
    const observer = new MutationObserver(() => {
      fixLokaliseImages();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Load Lokalise LiveJS script
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://app.lokalise.com/live-js/script.min.js?${Date.now()}`;
    document.body.appendChild(script);
  }, [enabled, projectId, i18n.language]);

  // Update locale when language changes
  useEffect(() => {
    if (enabled && projectId && window.LOKALISE_CONFIG) {
      window.LOKALISE_CONFIG.locale = i18n.language;

      document.dispatchEvent(new CustomEvent("lokalise-update-elements"));
    }
  }, [enabled, projectId, i18n.language]);

  return null;
}
